import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ============================================================
// LOAD RAZORPAY CHECKOUT
// ============================================================

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload = () => resolve(true);

    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
}

// ============================================================
// CATERING QUOTATION
// ============================================================

export default function CateringQuotation() {
  const { enquiryId } = useParams();

  const [data, setData] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [accepting, setAccepting] =
    useState(false);

  const [paying, setPaying] =
    useState(false);

  // ==========================================================
  // LOAD QUOTATION
  // ==========================================================

  useEffect(() => {
    const loadQuotation = async () => {
      try {
        const response = await api.get(
          `/catering/${enquiryId}/quotation`
        );

        setData(response.data);
      } catch (error) {
        console.error(
          "QUOTATION LOAD ERROR:",
          error
        );

        toast.error(
          error?.response?.data?.detail ||
            "Unable to load quotation"
        );
      } finally {
        setLoading(false);
      }
    };

    loadQuotation();
  }, [enquiryId]);

  // ==========================================================
  // ACCEPT QUOTATION
  // ==========================================================

  const acceptQuotation = async () => {
    try {
      setAccepting(true);

      const response = await api.post(
        `/catering/${enquiryId}/accept`
      );

      toast.success(
        "Quotation accepted successfully!"
      );

      setData(response.data);
    } catch (error) {
      console.error(
        "ACCEPT QUOTATION ERROR:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Failed to accept quotation"
      );
    } finally {
      setAccepting(false);
    }
  };

  // ==========================================================
  // PAY CATERING ADVANCE
  // ==========================================================

  const handlePayAdvance = async () => {
    try {
      setPaying(true);

      // ------------------------------------------------------
      // LOAD RAZORPAY
      // ------------------------------------------------------

      const razorpayLoaded =
        await loadRazorpay();

      if (!razorpayLoaded) {
        throw new Error(
          "Unable to load Razorpay. Please check your internet connection and try again."
        );
      }

      // ------------------------------------------------------
      // CREATE RAZORPAY ORDER
      // ------------------------------------------------------

      const response = await api.post(
        `/catering/${enquiryId}/razorpay/create`
      );

      const razorpayData =
        response.data;

      console.log(
        "CATERING RAZORPAY DATA:",
        razorpayData
      );

      // ------------------------------------------------------
      // VALIDATE RESPONSE
      // ------------------------------------------------------

      if (
        !razorpayData?.key_id ||
        !razorpayData?.razorpay_order_id
      ) {
        throw new Error(
          "Invalid Razorpay response from server."
        );
      }

      // ------------------------------------------------------
      // RAZORPAY OPTIONS
      // ------------------------------------------------------

      const razorpayOptions = {
        key: razorpayData.key_id,

        amount:
          razorpayData.amount,

        currency:
          razorpayData.currency || "INR",

        name: "Ice Cream Paradise",

        description:
          "Catering Advance Payment",

        order_id:
          razorpayData.razorpay_order_id,

        prefill: {
          name:
            razorpayData.customer_name ||
            data?.name ||
            "",

          email:
            razorpayData.customer_email ||
            "",

          contact:
            razorpayData.customer_phone ||
            "",
        },

        notes: {
          catering_enquiry_id:
            enquiryId,
        },

        theme: {
          color: "#1F5F5C",
        },

        // ----------------------------------------------------
        // PAYMENT SUCCESS
        // ----------------------------------------------------

        handler:
          async function (paymentResponse) {
            try {
              console.log(
                "CATERING PAYMENT RESPONSE:",
                paymentResponse
              );

              // ----------------------------------------------
              // VERIFY PAYMENT ON BACKEND
              // ----------------------------------------------

              const verifyResponse =
                await api.post(
                  `/catering/${enquiryId}/razorpay/verify`,
                  {
                    razorpay_order_id:
                      paymentResponse.razorpay_order_id,

                    razorpay_payment_id:
                      paymentResponse.razorpay_payment_id,

                    razorpay_signature:
                      paymentResponse.razorpay_signature,
                  }
                );

              console.log(
                "CATERING PAYMENT VERIFIED:",
                verifyResponse.data
              );

              // ----------------------------------------------
              // UPDATE PAGE
              // ----------------------------------------------

              setData(
                verifyResponse.data
              );

              toast.success(
                "Advance payment successful!"
              );

            } catch (error) {
              console.error(
                "CATERING PAYMENT VERIFICATION ERROR:",
                error
              );

              toast.error(
                error?.response?.data?.detail ||
                  "Payment verification failed. Please contact support."
              );
            } finally {
              setPaying(false);
            }
          },

        // ----------------------------------------------------
        // PAYMENT WINDOW CLOSED
        // ----------------------------------------------------

        modal: {
          ondismiss: function () {
            setPaying(false);

            toast.error(
              "Payment cancelled."
            );
          },
        },
      };

      // ------------------------------------------------------
      // OPEN RAZORPAY
      // ------------------------------------------------------

      const razorpay =
        new window.Razorpay(
          razorpayOptions
        );

      // ------------------------------------------------------
      // PAYMENT FAILED
      // ------------------------------------------------------

      razorpay.on(
        "payment.failed",
        (failureResponse) => {
          console.error(
            "CATERING RAZORPAY PAYMENT FAILED:",
            failureResponse
          );

          setPaying(false);

          toast.error(
            failureResponse?.error
              ?.description ||
              "Payment failed. Please try again."
          );
        }
      );

      razorpay.open();

    } catch (error) {
      console.error(
        "CATERING PAYMENT ERROR:",
        error
      );

      setPaying(false);

      toast.error(
        error?.response?.data?.detail ||
          error?.message ||
          "Unable to start payment."
      );
    }
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20">
        Loading quotation...
      </div>
    );
  }

  // ==========================================================
  // NO DATA
  // ==========================================================

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20">

        <h1 className="font-heading text-3xl">
          Quotation not available
        </h1>

        <p className="text-[#B7D8D5] mt-2">
          Please contact Ice Cream Paradise.
        </p>

      </div>
    );
  }

  const q = data.quotation;

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mb-8">

        <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
          Catering
        </div>

        <h1 className="font-heading text-4xl mt-2">
          Your Catering Quotation
        </h1>

        <p className="text-[#B7D8D5] mt-2">
          Hello {data.name}, here is your quotation.
        </p>

      </div>

      {/* ======================================================
          EVENT DETAILS
      ====================================================== */}

      <div className="glass rounded-3xl p-6 mb-6">

        <h2 className="font-heading text-2xl mb-4">
          Event Details
        </h2>

        <div className="grid md:grid-cols-2 gap-4 text-sm">

          {/* EVENT */}

          <div>
            <span className="text-[#B7D8D5]">
              Event
            </span>

            <div className="font-medium mt-1">
              {data.event_type}
            </div>
          </div>

          {/* GUESTS */}

          <div>
            <span className="text-[#B7D8D5]">
              Guests
            </span>

            <div className="font-medium mt-1">
              {data.guests}
            </div>
          </div>

          {/* DATE */}

          <div>
            <span className="text-[#B7D8D5]">
              Date
            </span>

            <div className="font-medium mt-1">
              {data.event_date}
            </div>
          </div>

          {/* TIME */}

          <div>
            <span className="text-[#B7D8D5]">
              Time
            </span>

            <div className="font-medium mt-1">
              {data.event_time ||
                "Not specified"}
            </div>
          </div>

          {/* LOCATION */}

          <div className="md:col-span-2">

            <span className="text-[#B7D8D5]">
              Location
            </span>

            <div className="font-medium mt-1">
              {data.location}
            </div>

          </div>

        </div>

      </div>

      {/* ======================================================
          QUOTATION
      ====================================================== */}

      <div className="glass rounded-3xl p-6">

        <h2 className="font-heading text-2xl mb-5">
          Price Details
        </h2>

        <div className="space-y-3 text-sm">

          {/* ICE CREAM */}

          <div className="flex justify-between">

            <span>
              Ice Cream
            </span>

            <span>
              ₹
              {Number(
                q.ice_cream_amount || 0
              ).toLocaleString("en-IN")}
            </span>

          </div>

          {/* STAFF */}

          <div className="flex justify-between">

            <span>
              Staff
            </span>

            <span>
              ₹
              {Number(
                q.staff_amount || 0
              ).toLocaleString("en-IN")}
            </span>

          </div>

          {/* TRANSPORT */}

          <div className="flex justify-between">

            <span>
              Transport
            </span>

            <span>
              ₹
              {Number(
                q.transport_amount || 0
              ).toLocaleString("en-IN")}
            </span>

          </div>

          {/* OTHER */}

          <div className="flex justify-between">

            <span>
              Other
            </span>

            <span>
              ₹
              {Number(
                q.other_amount || 0
              ).toLocaleString("en-IN")}
            </span>

          </div>

        </div>

        {/* ====================================================
            TOTALS
        ==================================================== */}

        <div className="border-t mt-5 pt-5 space-y-3">

          {/* TOTAL */}

          <div className="flex justify-between text-lg">

            <span>
              Total
            </span>

            <strong>
              ₹
              {Number(
                q.total_amount || 0
              ).toLocaleString("en-IN")}
            </strong>

          </div>

          {/* ADVANCE */}

          <div className="flex justify-between">

            <span>
              Advance Payment
            </span>

            <strong>
              ₹
              {Number(
                q.advance_amount || 0
              ).toLocaleString("en-IN")}
            </strong>

          </div>

          {/* BALANCE */}

          <div className="flex justify-between">

            <span>
              Balance
            </span>

            <strong className="text-[#B8E14A]">
              ₹
              {Number(
                q.balance_amount || 0
              ).toLocaleString("en-IN")}
            </strong>

          </div>

        </div>

        {/* ====================================================
            NOTES
        ==================================================== */}

        {q.notes && (
          <div className="mt-5 rounded-2xl bg-white/40 p-4 text-sm">

            <strong>
              Notes:
            </strong>{" "}

            {q.notes}

          </div>
        )}

        {/* ====================================================
            ACTIONS
        ==================================================== */}

        <div className="mt-7">

          {/* ==================================================
              QUOTATION SENT
          ================================================== */}

          {data.status ===
          "quotation_sent" ? (

            <button
              onClick={acceptQuotation}
              disabled={accepting}
              className="btn-primary w-full justify-center"
            >

              {accepting
                ? "Accepting..."
                : "Accept Quotation"}

            </button>

          ) : data.status ===
            "confirmed" ? (

            <div className="space-y-4">

              {/* ACCEPTED */}

              <div className="rounded-2xl bg-green-100 border border-green-300 text-green-800 px-5 py-4 text-center">

                ✓ Quotation accepted

              </div>

              {/* PAYMENT COMPLETE */}

              {data.payment_status ===
              "verified" ? (

                <div className="rounded-2xl bg-green-100 border border-green-300 text-green-800 px-5 py-4 text-center font-semibold">

                  ✓ Advance Payment Completed

                </div>

              ) : (

                /* PAY ADVANCE */

                <button
                  onClick={handlePayAdvance}
                  disabled={paying}
                  className="btn-primary w-full justify-center"
                >

                  {paying
                    ? "Opening Payment..."
                    : `Pay ₹${Number(
                        q.advance_amount || 0
                      ).toLocaleString(
                        "en-IN"
                      )} Advance`}

                </button>

              )}

            </div>

          ) : data.status ===
            "advance_paid" ? (

            <div className="rounded-2xl bg-green-100 border border-green-300 text-green-800 px-5 py-4 text-center font-semibold">

              ✓ Catering Advance Paid

            </div>

          ) : (

            <div className="rounded-2xl bg-white/40 px-5 py-4 text-center text-sm">

              Current status:{" "}

              {String(
                data.status || ""
              ).replace(
                /_/g,
                " "
              )}

            </div>

          )}

        </div>

      </div>

    </div>
  );
}